import { useState, useEffect, ReactElement } from "react";
import { useNavigate, Outlet } from "react-router-dom";
import BlockableNavLink from "../navigation/BlockableNavLink";
import { signOut } from "aws-amplify/auth";
import useAuth from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { useBottomScroll } from "../../../hooks/useBottomScrollContext";
import useUserProfile from "../../../hooks/useUserProfile";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { useUseCases } from "@/context/UseCasesProvider";
import { isProduction } from "@/utils/environment";

import vistaflowPicto from "../../../vistaflow-picto.png";
import envDevBackground from "../../../env-dev.jpg";
import { IoMenu } from "react-icons/io5";
import { PiAirTrafficControl } from "react-icons/pi";
import { RiLogoutCircleRLine } from "react-icons/ri";
import { TbDeviceDesktopAnalytics, TbCloudDollar, TbSettingsCode, TbHeadset } from "react-icons/tb";
import { AiOutlineUsergroupAdd } from "react-icons/ai";
import { FaBuildingUser } from "react-icons/fa6";
import { BsDatabaseDown } from "react-icons/bs";
import NotificationToast from "./NotificationToast";

import Header from "../header/Header";

interface MenuItem {
  title: string;
  icon: ReactElement;
  url: string;
  restricted: boolean;
  supervisorOnly?: boolean;
  adminOnly?: boolean;
  adminVistaFlowOnly?: boolean;
  displayFeatures?: string;
  id?: string;
}
interface SettingsInterface {
  settings: any;
}

const Layout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuth, auth } = useAuth();
  const { isDataEntrySupervisor, isAdminVistaflow, isAdmin } = useUserProfile();
  const settingsInterface = useUseCases() as SettingsInterface;
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState<boolean>(localStorage.getItem("menuOpen") === "true");
  const [username, setUsername] = useState<string>("");
  
  const menuItems: MenuItem[] = [
    {
      title: t("control_tower"),
      icon: <PiAirTrafficControl />,
      url: "/orders/?refresh=true",
      restricted: true,
    },
    {
      title: t("dashboard"),
      icon: <TbDeviceDesktopAnalytics />,
      url: "/dashboard/",
      restricted: true,
      supervisorOnly: true,
    },
    {
      title: t("users"),
      icon: <AiOutlineUsergroupAdd />,
      url: "/users/",
      restricted: true,
      supervisorOnly: true,
    },
    {
      title: t("billing"),
      icon: <TbCloudDollar />,
      url: "/billing/",
      restricted: true,
      adminVistaFlowOnly: true,
    },
    {
      title: t("database"),
      icon: <BsDatabaseDown />,
      url: "/database/",
      restricted: true,
      displayFeatures: "database",
    },
    {
      title: t("customers"),
      icon: <FaBuildingUser />,
      url: "/customers/",
      restricted: true,
      adminOnly: true,
    },
    {
      title: t("settings"),
      icon: <TbSettingsCode />,
      url: "/settings/",
      restricted: true,
      adminVistaFlowOnly: true,
    },
  ];

  const handleLogOut = async () => {
    try {
      await signOut();
      setAuth({ accessToken: null, userFromCognito: null });
      navigate("/login", { replace: true });
      queryClient.clear();
    } catch (error) {
      console.log("Error during sign out:", error);
    }
  };

  useEffect(() => {
    if (auth?.userFromCognito?.username) {
      setUsername(auth.userFromCognito.username);
    }
  }, [auth]);

  useEffect(() => {
    localStorage.setItem("menuOpen", JSON.stringify(menuOpen));
  }, [menuOpen]);

  const { setAtBottom } = useBottomScroll();
  const handleScroll = (e) => {
    const bottom = (e.target.scrollTop / e.target.scrollHeight) * 100;
    const scrollHeight = e.target.scrollHeight;
    setAtBottom({ bottom, scrollHeight });
  };
  const filteredMenuItems = menuItems.filter(
    (item) =>
      (!item.supervisorOnly || (item.supervisorOnly && (isDataEntrySupervisor || isAdmin))) &&
      (!item.adminOnly || (item.adminOnly && isAdmin)) &&
      (!item.adminVistaFlowOnly || (item.adminVistaFlowOnly && isAdminVistaflow)) &&
      (!item.displayFeatures || settingsInterface?.settings?.display?.features?.[item.displayFeatures] === true)
  );

  const filteredMenuItemsWithIds = filteredMenuItems.map((item) => ({
    ...item,
    id: uuidv4(),
  }));

  const openSupportWidget = () => {
    if (globalThis.FreshworksWidget) {
      globalThis.FreshworksWidget("open");
    }
  };

  useEffect(() => {
    // Hide the default Freshworks launcher button
    if (globalThis.FreshworksWidget) {
      globalThis.FreshworksWidget("hide", "launcher");
    }
  }, []);

  return (
    <div className="max-w-full flex min-h-screen vistagreen">
      <div
        className={`${
          isProduction ? "bg-vistadark" : "bg-cover bg-center"
        } h-screen p-1 pt-8 ${menuOpen ? "w-72" : "w-20"} duration-200 flex flex-col fixed`}
        style={isProduction ? {} : { backgroundImage: `url(${envDevBackground})` }}
      >
        <div className={`${menuOpen ? "flex justify-center" : "inline-flex items-center ml-4"}`}>
          {isProduction ? (
            <img
              src={vistaflowPicto}
              alt={t("vistaflow_logo")}
              className={`w-10 origin-left self-center ${
                isProduction ? "" : "grayscale invert contrast-200 brightness-50"
              }`}
            />
          ) : (
            <span className="font-bold text-lg self-center">DEV</span>
          )}
          <h1
            className={`text-white text-4xl pl-3 font-['Nunito_Sans'] font-bold duration-500 ${
              !menuOpen && "scale-0"
            }`}
          >
            VistaFlow
          </h1>
        </div>
        <nav>
          <ul>
            {filteredMenuItemsWithIds.map((item) => (
              <li
                key={item.id}
                className={`${
                  isProduction ? "text-white" : "text-vistagrey"
                } text-sm flex items-center gap-x-4 rounded-md m-3 p-3 h-11`}
              >
                <BlockableNavLink to={`${item.url}`} className="block h-full">
                  <span className="text-2xl block float-left">{item.icon}</span>
                  <span className={`text-sm font-medium flex-1 pl-3 ${!menuOpen && "hidden"}`}>
                    {item.title}
                  </span>
                </BlockableNavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
      <div
        className={`${
          menuOpen ? "pl-72" : "pl-20"
        } h-screen duration-200 bg-vistagrey/10 overflow-auto min-h-screen flex-1`}
        onScroll={handleScroll}
        id="scrollElement"
      >
        {/* Header */}
        <div className="sticky flex h-20 bg-white border-b-[20px] border-vistagrey/10 top-0 z-50">
          
          <div className="flex w-full items-center">
            <div>
              <IoMenu
                onClick={() => setMenuOpen(!menuOpen)}
                className={`text-vistagrey bg-white text-3xl rounded-full -right-3 top-9 mx-5 cursor-pointer float-left ${
                  !menuOpen && "rotate-180"
                }`}
              />
            </div>
            {username && <Header user={username} env={isProduction} />}
            <button
              onClick={handleLogOut}
              className="bg-vistagreen h-full px-4 text-white text-xs cursor-pointer flex items-center gap-2"
            >
              <RiLogoutCircleRLine className="text-2xl" />
              {t("logout")}
            </button>
          </div>
        </div>
        <NotificationToast />
        <main className="mx-6">
          <Outlet />
        </main>
      </div>
      {!isAdminVistaflow && (
        <button
          onClick={openSupportWidget}
          className="fixed bottom-4 left-4 z-50 bg-gray-800 text-white p-3 rounded-full shadow-lg cursor-pointer hover:bg-gray-700"
        >
          <TbHeadset className="text-2xl" />
        </button>
      )}
    </div>
  );
};

export default Layout;
